import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

/**
 * Visually-hidden aria-live region that announces page changes to screen
 * readers. SPAs don't fire the native browser navigation beep, so SR users
 * have no audio cue when the route changes — this fills the gap.
 *
 * Labels go through i18next so non-English locales hear announcements in
 * their language (matches the rest of the app's i18n discipline).
 */
const PATH_TO_KEY: Array<[RegExp, string, string]> = [
  [/^\/$/, 'a11y.routes.home', 'Home'],
  [/^\/welcome$/, 'a11y.routes.welcome', 'Welcome'],
  [/^\/sign-in$/, 'a11y.routes.signIn', 'Sign in'],
  [/^\/sign-up$/, 'a11y.routes.signUp', 'Sign up'],
  [/^\/onboarding\/welcome$/, 'a11y.routes.onboardingWelcome', 'Onboarding — welcome'],
  [/^\/onboarding\/benefits$/, 'a11y.routes.onboardingBenefits', 'Onboarding — benefits'],
  [/^\/onboarding\/profile$/, 'a11y.routes.onboardingProfile', 'Onboarding — profile'],
  [/^\/chart\/create$/, 'a11y.routes.chartCreate', 'Create your chart'],
  [/^\/chart\/edit$/, 'a11y.routes.chartEdit', 'Edit your chart'],
  [/^\/chart\/me$/, 'a11y.routes.chartMe', 'Your chart'],
  [/^\/fortune$/, 'a11y.routes.fortune', 'Fortune'],
  [/^\/compatibility$/, 'a11y.routes.compatibility', 'Compatibility'],
  [/^\/compatibility\/history$/, 'a11y.routes.compatibilityHistory', 'Compatibility history'],
  [/^\/compatibility\/[^/]+$/, 'a11y.routes.compatibilityReading', 'Compatibility reading'],
  [/^\/auspicious$/, 'a11y.routes.auspicious', 'Auspicious dates'],
  [/^\/profile$/, 'a11y.routes.profile', 'Profile'],
  [/^\/chat$/, 'a11y.routes.chat', 'Chat'],
  [/^\/chat\/history$/, 'a11y.routes.chatHistory', 'Chat history'],
  [/^\/paywall$/, 'a11y.routes.paywall', 'Premium'],
];

function findEntry(pathname: string): { key: string; defaultLabel: string } {
  for (const [pattern, key, defaultLabel] of PATH_TO_KEY) {
    if (pattern.test(pathname)) return { key, defaultLabel };
  }
  return { key: 'a11y.routes.page', defaultLabel: 'Page' };
}

export function RouteAnnouncer() {
  const location = useLocation();
  const { t } = useTranslation();
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Defer one tick so the DOM update from the route swap settles before
    // we re-announce — otherwise some screen readers skip the message.
    const timer = window.setTimeout(() => {
      const { key, defaultLabel } = findEntry(location.pathname);
      const label = t(key, defaultLabel);
      setMessage(t('a11y.routes.loaded', '{{label}} loaded', { label }));
    }, 50);
    return () => window.clearTimeout(timer);
  }, [location.pathname, t]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {message}
    </div>
  );
}
